import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import type { IconName } from "./Icon";
import { cx } from "./basics";

/* ---------- modal: a bottom sheet on phones, a centred dialog from 640px ---------- */

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Keeps Tab and Shift+Tab inside an open dialog (UX-179): from the last control back to the
    first, and the other way round. Focus that's already outside (a dialog stacked on top) is
    left alone. */
function trapTab(e: KeyboardEvent, el: HTMLElement) {
  const active = document.activeElement;
  if (active && active !== el && !el.contains(active)) return;
  const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
  if (!items.length) return;
  const first = items[0]!;
  const last = items[items.length - 1]!;
  if (e.shiftKey && (active === first || active === el)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && active === last) {
    e.preventDefault();
    first.focus();
  }
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  wide,
  className,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    // On phones the sheet opens without popping the keyboard; on desktop the field marked
    // data-autofocus is focused (UX-180), else the first field.
    const phone = window.matchMedia?.("(max-width: 899.98px)").matches;
    const first =
      el?.querySelector<HTMLElement>("[data-autofocus]") ??
      el?.querySelector<HTMLElement>("input:not([type=checkbox]):not([type=radio]),select,textarea");
    if (first && !phone) first.focus();
    else el?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
      else if (e.key === "Tab" && el) trapTab(e, el);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      prev?.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className="modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        tabIndex={-1}
        className={cx("modal", wide && "wide", !footer && "nofoot", className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : undefined}
      >
        <div className="modal-h">
          <h2>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-b">{children}</div>
        {footer && <div className="modal-f">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ---------- toasts ---------- */

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** Buttons beside the message ("Undo", "View in Recycle bin"); any of them closes the toast. */
  actions?: ToastAction[];
  /** How long it stays, in ms. */
  ms?: number;
}

interface ToastItem {
  id: number;
  msg: string;
  kind: "" | "err" | "warn";
  onClick?: () => void;
  actions?: ToastAction[];
}

type Toast = (msg: string, kind?: "" | "err" | "warn", onClick?: () => void, options?: ToastOptions) => void;

const ToastCtx = createContext<Toast>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const toast = useCallback<Toast>((msg, kind = "", onClick, options) => {
    const id = ++seq.current;
    setItems((xs) => [...xs.slice(-2), { id, msg, kind, onClick, actions: options?.actions }]);
    // One to act on stays a little longer.
    const ms =
      options?.ms ?? (kind === "err" || kind === "warn" || onClick || options?.actions?.length ? 5200 : 3600);
    setTimeout(() => setItems((xs) => xs.filter((x) => x.id !== id)), ms);
  }, []);
  const close = (id: number) => setItems((xs) => xs.filter((x) => x.id !== id));
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      {/* A log, not a status: status is atomic, so each new toast had the whole region read
          again, repeating a toast still on screen. Here only the toast just added is read. */}
      <div id="toasts" role="log" aria-live="polite" aria-atomic="false" aria-relevant="additions">
        {items.map((t) =>
          t.actions?.length ? (
            <div key={t.id} className={cx("toast", "toast-acts", t.kind)}>
              <span>{t.msg}</span>
              {t.actions.map((a) => (
                <button
                  key={a.label}
                  type="button"
                  className="link-btn"
                  onClick={() => {
                    close(t.id);
                    a.onClick();
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          ) : t.onClick ? (
            <button
              key={t.id}
              type="button"
              className={cx("toast", "toast-act", t.kind)}
              onClick={() => {
                close(t.id);
                t.onClick?.();
              }}
            >
              {t.msg}
            </button>
          ) : (
            <div key={t.id} className={cx("toast", t.kind)}>
              {t.msg}
            </div>
          ),
        )}
      </div>
    </ToastCtx.Provider>
  );
}

/** toast("Saved"), toast("Couldn't save", "err"), one that opens something when tapped, or one
    with buttons: toast("Deleted", "", undefined, { actions: [{ label: "Undo", onClick }] }). */
export function useToast() {
  return useContext(ToastCtx);
}

/* ---------- phone chrome: bottom tab bar and sticky action bar ---------- */

export interface TabBarItem {
  key: string;
  label: string;
  icon: IconName;
  active: boolean;
  /** A count shown on the icon (portal: quotes to review), or a short label such as "1/3". */
  badge?: number | string;
  onSelect: () => void;
}

export function BottomTabBar({ items, label = "Sections" }: { items: TabBarItem[]; label?: string }) {
  return (
    <nav className="tabbar" aria-label={label}>
      {items.map((t) => (
        <button
          key={t.key}
          type="button"
          className={cx("tb", t.active && "on")}
          aria-current={t.active ? "page" : undefined}
          onClick={t.onSelect}
        >
          <span className="tb-i">
            <Icon name={t.icon} />
            {!!t.badge && <span className="tb-dot">{t.badge}</span>}
          </span>
          <span className="tb-l">{t.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** The thumb-reach action bar on phones (hidden from 900px, where the page head shows the same
    buttons). Sets --bar-h on <body> so the page's bottom padding clears it. */
export function ActionBar({ info, children }: { info?: ReactNode; children?: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => document.body.style.setProperty("--bar-h", `${el.offsetHeight}px`);
    sync();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(sync) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.body.style.setProperty("--bar-h", "0px");
    };
  }, []);
  return createPortal(
    <div ref={ref} className="actbar" role="toolbar" aria-label="Page actions">
      {info}
      {children}
    </div>,
    document.body,
  );
}

/** Label + value at the start of the action bar ("Total · CA$12,400.00"). */
export function ActionInfo({
  label,
  value,
  sub,
  wide,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  wide?: boolean;
}) {
  return (
    <div className={cx("ab-info", wide && "wide")}>
      <span>{label}</span>
      <strong>{value}</strong>
      {sub && <small>{sub}</small>}
    </div>
  );
}
